import {
    ArrowLeftOutlined, CopyOutlined, DeleteOutlined, EditOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, Flex, Grid, Space, Splitter, theme, Typography,
} from 'antd';
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useParams,
} from 'react-router-dom';

import {
    managerMutations, managerQueries, projectsQueries, scriptsMutations, scriptsQueries,
} from '@/api';
import {
    CrossSellsCarousel, DeleteProjectModal, FieldsList, getActionControlStyleVars, ObjectionsList, RoleGuard, ScriptActiveBadge, ScriptChangesModal, ScriptChangesNotification, ScriptCommentSection, ScriptCopyModal, ScriptMainInfo,
} from '@/components';
import {
    AppRoute, UserRole,
} from '@/constants';
import {
    useBackToOrList, usePopupQueryParams,
} from '@/hooks';
import { useUIStore, useUserStore } from '@/stores';

const AUTO_OPEN_INSTRUCTIONS: Record<string, string[]> = {
    default: ['Апсейл'],
    'cross-sale': ['Характеристики'],
};

export const ScriptDetailsPage = () => {
    const { id, projectId } = useParams<{ id: string; projectId: string }>();
    const scriptsListPath = projectId ? `/projects/${projectId}` : AppRoute.Scripts;
    const handleBack = useBackToOrList(scriptsListPath);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const popupQuery = usePopupQueryParams();
    const { token } = theme.useToken();
    const { user } = useUserStore();
    const screens = Grid.useBreakpoint();
    const isMobileDetailLayout = screens.lg === false;

    const detailCardStyles = useMemo(() => {
        if (!isMobileDetailLayout) {
            return undefined;
        }
        return {
            header: {
                paddingTop: 16,
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
                overflow: 'visible',
            },
            title: {
                display: 'block',
                overflow: 'visible',
                whiteSpace: 'normal',
                textOverflow: 'unset',
                width: '100%',
                maxWidth: '100%',
            },
            body: {
                padding: 16,
            },
        };
    }, [isMobileDetailLayout]);

    const isManager = useMemo(() => user?.role === UserRole.Manager, [user?.role]);
    const { setPageTitle } = useUIStore();

    const projectQuery = useMemo(() => {
        return isManager
            ? managerQueries.getManagerProject(projectId || '')
            : projectsQueries.getOneProject(projectId || '');
    }, [isManager, projectId]);

    const { data: project } = useQuery({ ...projectQuery, enabled: !!projectId });

    useEffect(() => {
        setPageTitle(project?.title ?? null);
        return () => setPageTitle(null);
    }, [project?.title, setPageTitle]);

    const scriptQuery = useMemo(() => {
        return isManager
            ? managerQueries.getManagerScript(id || '')
            : scriptsQueries.getOneScript(id || '', projectId);
    }, [isManager, id, projectId]);

    const { data: script, isLoading, isPlaceholderData } = useQuery(scriptQuery);

    const deleteMutationOptions = useMemo(
        () => script && projectId
            ? scriptsMutations.delete(projectId, script.id)
            : scriptsMutations.delete('', ''),
        [script, projectId],
    );

    const { mutate: deleteScript, isPending: isDeleting } = useMutation(deleteMutationOptions);

    const handleDelete = useCallback(() => {
        if (script) {
            setDeleteModalOpen(true);
        }
    }, [script]);

    const handleDeleteConfirm = useCallback(() => {
        if (script) {
            deleteScript(undefined, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    handleBack();
                },
            });
        }
    }, [script, deleteScript, handleBack]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteModalOpen(false);
    }, []);

    const acknowledgeChangesMutationOptions = useMemo(
        () => managerMutations.acknowledgeScriptChanges(id || ''),
        [id],
    );

    const { mutate: acknowledgeChanges, isPending: isAcknowledging } = useMutation(acknowledgeChangesMutationOptions);

    const handleOpenChangesModal = useCallback(() => {
        void popupQuery.open({ mode: 'changes' });
    }, [popupQuery]);

    const handleCloseChangesModal = useCallback(() => {
        void popupQuery.close();
    }, [popupQuery]);

    const handleAcknowledgeChanges = useCallback(() => {
        if (id) {
            acknowledgeChanges(undefined, {
                onSuccess: () => {
                    void popupQuery.close();
                },
            });
        }
    }, [acknowledgeChanges, id, popupQuery]);

    const unacknowledgedChanges = script?.unacknowledged_changes || [];
    const hasUnacknowledgedChanges = unacknowledgedChanges.length > 0;
    const lastChangedAt = hasUnacknowledgedChanges ? (unacknowledgedChanges[0]?.changed_at || null) : null;

    const handleImagePreviewOpen = useCallback(() => {
        void popupQuery.open({
            mode: 'image',
            imageIndex: popupQuery.imageIndex ?? 0,
        });
    }, [popupQuery]);

    const handleImagePreviewChange = useCallback((current: number) => {
        void popupQuery.open({
            mode: 'image',
            imageIndex: current,
        });
    }, [popupQuery]);

    const handleImagePreviewVisibleChange = useCallback((visible: boolean) => {
        if (visible) {
            return;
        }

        void popupQuery.close();
    }, [popupQuery]);

    const scriptMainInfoProps = useMemo(() => ({
        script,
        loading: isLoading,
        previewVisible: popupQuery.isMode('image'),
        previewIndex: popupQuery.imageIndex ?? 0,
        onPreviewOpen: handleImagePreviewOpen,
        onPreviewChange: handleImagePreviewChange,
        onPreviewVisibleChange: handleImagePreviewVisibleChange,
    }), [
        handleImagePreviewChange,
        handleImagePreviewOpen,
        handleImagePreviewVisibleChange,
        isLoading,
        popupQuery,
        script,
    ]);

    return (
        <>
            <Helmet>
                <title>Деталі скрипта</title>
            </Helmet>

            {isManager && !isLoading && !isPlaceholderData && hasUnacknowledgedChanges && (
                <Flex justify="center" style={{ width: '100%' }}>
                    <div style={{ width: '50%', minWidth: 280 }}>
                        <ScriptChangesNotification
                            onOpenDetails={handleOpenChangesModal}
                            onAcknowledge={handleAcknowledgeChanges}
                            acknowledgeLoading={isAcknowledging}
                            lastChangedAt={lastChangedAt}
                        />
                    </div>
                </Flex>
            )}

            <Card
                loading={isLoading}
                className="detail-page-card"
                styles={detailCardStyles}
                title={
                    <Flex
                        vertical={isMobileDetailLayout}
                        align={isMobileDetailLayout ? 'stretch' : 'center'}
                        justify="space-between"
                        gap="middle"
                        wrap="wrap"
                        style={{ width: '100%' }}
                    >
                        <Flex align="center" gap="middle" style={{ minWidth: 0, flex: isMobileDetailLayout ? undefined : 1 }}>
                            <Link
                                to={scriptsListPath}
                                aria-label="Назад"
                                className="app-action-control app-action-control--icon"
                                style={getActionControlStyleVars(token, {
                                    variant: 'text',
                                    bordered: false,
                                    iconOnly: true,
                                })}
                            >
                                <ArrowLeftOutlined style={{ fontSize: 16 }} />
                            </Link>
                            <Space align="center" size="middle" wrap>
                                <Typography.Title
                                    level={4}
                                    ellipsis={false}
                                    style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                    <span>
                                        {script?.title || 'Деталі скрипта'}
                                    </span>
                                </Typography.Title>
                                {script && (
                                    <ScriptActiveBadge isActive={Boolean(script.is_active ?? true)} />
                                )}
                            </Space>
                        </Flex>
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                            <Flex gap="middle" wrap="wrap" style={{ marginLeft: isMobileDetailLayout ? 0 : 'auto', justifyContent: isMobileDetailLayout ? 'flex-start' : undefined }}>
                                <Link
                                    to={`/projects/${projectId}/scripts/${id}/edit`}
                                    className="app-action-control"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'primary',
                                    })}
                                >
                                    <EditOutlined style={{ fontSize: 16 }} />
                                    Редагувати
                                </Link>
                                <Button
                                    icon={<CopyOutlined />}
                                    onClick={() => {
                                        void popupQuery.open({ mode: 'copy' });
                                    }}
                                >
                                    Копіювати
                                </Button>
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                >
                                    Видалити
                                </Button>
                            </Flex>
                        </RoleGuard>
                    </Flex>
                }
            >
                {isMobileDetailLayout ? (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <ScriptMainInfo {...scriptMainInfoProps} />
                                <CrossSellsCarousel scripts={script?.cross_sales || []} />
                                <FieldsList
                                    key={script?.id}
                                    fields={(script?.instructions as { name: string; value: string; }[] | null | undefined) || null}
                                    renderAsHtml={true}
                                    defaultOpenNames={AUTO_OPEN_INSTRUCTIONS[script?.type ?? ''] ?? []}
                                />
                            </Space>
                        </div>
                        <div>
                            <ObjectionsList />
                        </div>
                    </Space>
                ) : (
                    <Splitter>
                        <Splitter.Panel>
                            <div style={{ paddingRight: token.paddingXS }}>
                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                    <ScriptMainInfo {...scriptMainInfoProps} />
                                    <CrossSellsCarousel scripts={script?.cross_sales || []} />
                                    <FieldsList
                                        key={script?.id}
                                        fields={(script?.instructions as { name: string; value: string; }[] | null | undefined) || null}
                                        renderAsHtml={true}
                                        defaultOpenNames={AUTO_OPEN_INSTRUCTIONS[script?.type ?? ''] ?? []}
                                    />
                                </Space>
                            </div>
                        </Splitter.Panel>
                        <Splitter.Panel defaultSize="30%" min="25%" max="40%">
                            <div style={{ paddingLeft: token.paddingXS }}>
                                <ObjectionsList />
                            </div>
                        </Splitter.Panel>
                    </Splitter>
                )}
            </Card>

            {id && (
                <Card
                    className="detail-page-card"
                    style={{ marginTop: token.marginLG }}
                    styles={detailCardStyles}
                >
                    <ScriptCommentSection scriptId={id} />
                </Card>
            )}

            <DeleteProjectModal
                open={deleteModalOpen}
                entityName={script?.title}
                entityType="script"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={isDeleting}
            />

            {script && (
                <ScriptCopyModal
                    open={popupQuery.isMode('copy')}
                    onClose={() => {
                        void popupQuery.close();
                    }}
                    sourceScriptId={script.id}
                    scriptTitle={script.title}
                />
            )}

            {isManager && hasUnacknowledgedChanges && (
                <>
                    <ScriptChangesModal
                        open={popupQuery.isMode('changes')}
                        changes={unacknowledgedChanges}
                        onConfirm={handleAcknowledgeChanges}
                        onCancel={handleCloseChangesModal}
                        loading={isAcknowledging}
                    />
                </>
            )}
        </>
    );
};
